<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  VISA_REGIONS, VISA_TERMS, VISA_STATUSES,
  syncVisaType, countryNames, filterCountries, storedCountryName, displayCountryName,
} from '../../domain/visa.js';
import DateField from '../DateField.vue';

/* Die Felder eines Visums — für das Anlegen (Entwurf) und das Bearbeiten
   (bestehender Eintrag) gleichermaßen. Das Objekt wird direkt geändert, wie
   überall in den Akten; gespeichert wird erst über "Speichern" im Panel.

   Reihenfolge: Region → (Land) → Dauer → Status. Nur bei "erteilt" folgen
   Zeitraum, Ausstellung und Einreise. Beantragt und abgelehnt brauchen nichts
   weiter. Werte, die bei "erteilt" schon eingetragen waren, bleiben im
   Datensatz erhalten, wenn der Status wieder gewechselt wird. */
const props = defineProps({
  visa: { type: Object, required: true },
});

const { locale } = useI18n();

/* Alter Eintrag ohne Region: nur ein Freitext-Typ. Er bleibt bearbeitbar, bis
   eine Region gewählt wird. */
const isLegacy = computed(() => !props.visa.region && !!props.visa.type);

function setRegion(r) {
  props.visa.region = r;
  if (r !== 'other') props.visa.country = '';
  syncVisaType(props.visa);
}
function setTerm(term) {
  props.visa.term = term;
  syncVisaType(props.visa);
}

/* --- Länderfeld ---------------------------------------------------------- */
const list = computed(() => countryNames(locale.value));
const countryText = ref(displayCountryName(props.visa.country, locale.value));
const focused = ref(false);
const suggestions = computed(() =>
  focused.value ? filterCountries(list.value, countryText.value) : []
);

function onCountryInput() {
  props.visa.country = storedCountryName(countryText.value, locale.value);
  syncVisaType(props.visa);
}
function pickCountry(c) {
  countryText.value = c.name;
  focused.value = false;
  onCountryInput();
}
</script>

<template>
  <div class="vf">
    <input
      v-if="isLegacy"
      v-model="visa.type"
      type="text"
      :placeholder="$t('documents.visaType')"
    />

    <div class="vf-block">
      <div class="vf-label">{{ $t('documents.visaRegionLabel') }}</div>
      <div class="vf-chips" role="group" :aria-label="$t('documents.visaRegionLabel')">
        <button
          v-for="r in VISA_REGIONS"
          :key="r"
          type="button"
          class="vchip"
          :class="{ on: visa.region === r }"
          :aria-pressed="visa.region === r"
          @click="setRegion(r)"
        >
          {{ $t(`documents.visaRegion.${r}`) }}
        </button>
      </div>
    </div>

    <div v-if="visa.region === 'other'" class="vf-block">
      <label class="vf-label" for="visa-country">{{ $t('documents.visaCountry') }}</label>
      <div class="vf-combo">
        <input
          id="visa-country"
          v-model="countryText"
          type="text"
          autocomplete="off"
          :placeholder="$t('documents.visaCountryPlaceholder')"
          @focus="focused = true"
          @blur="focused = false"
          @input="onCountryInput"
        />
        <ul v-if="suggestions.length" class="vf-suggest">
          <!-- mousedown statt click: Der Klick käme erst nach dem blur, und die
               Liste wäre dann schon weg. -->
          <li v-for="c in suggestions" :key="c.code" @mousedown.prevent="pickCountry(c)">
            {{ c.name }}
          </li>
        </ul>
      </div>
      <div class="vf-hint">{{ $t('documents.visaCountryHint') }}</div>
    </div>

    <div class="vf-block">
      <div class="vf-label">{{ $t('documents.visaTermLabel') }}</div>
      <div class="vf-chips" role="group" :aria-label="$t('documents.visaTermLabel')">
        <button
          v-for="term in VISA_TERMS"
          :key="term"
          type="button"
          class="vchip"
          :class="{ on: visa.term === term }"
          :aria-pressed="visa.term === term"
          @click="setTerm(term)"
        >
          {{ $t(`documents.visaTerm.${term}`) }}
        </button>
      </div>
    </div>

    <div class="vf-block">
      <div class="vf-label">{{ $t('documents.visaStatusLabel') }}</div>
      <div class="vf-chips" role="group" :aria-label="$t('documents.visaStatusLabel')">
        <button
          v-for="s in VISA_STATUSES"
          :key="s"
          type="button"
          class="vchip"
          :class="[`st-${s}`, { on: visa.status === s }]"
          :aria-pressed="visa.status === s"
          @click="visa.status = s"
        >
          {{ $t(`documents.visaStatus.${s}`) }}
        </button>
      </div>
    </div>

    <template v-if="visa.status === 'granted'">
      <fieldset class="vf-group">
        <legend>{{ $t('documents.visaGroupValidity') }}</legend>
        <div class="vf-grid">
          <label class="dated">
            <span>{{ $t('documents.validFrom') }}</span>
            <DateField v-model="visa.from" />
          </label>
          <label class="dated">
            <span>{{ $t('documents.validUntil') }}</span>
            <DateField v-model="visa.until" />
          </label>
        </div>
        <label class="dated">
          <span>{{ $t('documents.visaStay') }}</span>
          <input v-model="visa.duration" type="text" :placeholder="$t('documents.visaStayPlaceholder')" />
        </label>
      </fieldset>

      <fieldset class="vf-group">
        <legend>{{ $t('documents.visaGroupIssue') }}</legend>
        <div class="vf-grid">
          <label class="dated">
            <span>{{ $t('documents.appliedOn') }}</span>
            <DateField v-model="visa.appdate" />
          </label>
          <label class="dated">
            <span>{{ $t('documents.issuedOn') }}</span>
            <DateField v-model="visa.issued" />
          </label>
        </div>
      </fieldset>

      <fieldset class="vf-group">
        <legend>{{ $t('documents.visaGroupEntry') }}</legend>
        <label class="check">
          <input v-model="visa.multi" type="checkbox" />
          {{ $t('documents.multiEntry') }}
        </label>
      </fieldset>
    </template>
  </div>
</template>

<style scoped>
.vf {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.vf-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.vf-label {
  font-size: 0.66rem;
  color: var(--dim);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.vf-hint {
  font-size: 0.72rem;
  color: var(--dim);
}
.vf-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.vchip {
  padding: 7px 12px;
  border: 1px solid var(--border);
  border-radius: 99px;
  background: var(--card-hover);
  color: var(--muted);
  font-family: var(--sans);
  font-size: 0.78rem;
  cursor: pointer;
}
.vchip:hover {
  color: var(--text);
}
.vchip.on {
  color: var(--green);
  border-color: var(--green-dim);
  background: var(--green-bg);
}
.vchip.st-applied.on {
  color: var(--amber);
  border-color: var(--amber);
  background: var(--amber-bg);
}
.vchip.st-rejected.on {
  color: var(--red);
  border-color: var(--red);
  background: var(--red-bg);
}
.vf-combo {
  position: relative;
}
.vf-combo input {
  width: 100%;
  box-sizing: border-box;
}
.vf-suggest {
  list-style: none;
  margin: 4px 0 0;
  padding: 4px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 8px;
  max-height: 210px;
  overflow-y: auto;
}
.vf-suggest li {
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 0.82rem;
  color: var(--text);
  cursor: pointer;
}
.vf-suggest li:hover {
  background: var(--card-hover);
}
.vf-group {
  margin: 0;
  padding: 10px 12px 12px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.vf-group legend {
  padding: 0 6px;
  font-size: 0.66rem;
  color: var(--green);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.vf-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}
.dated {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 0.66rem;
  color: var(--dim);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 0;
}
.dated input {
  width: 100%;
  box-sizing: border-box;
}
.check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: var(--muted);
}
</style>
